import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Audience, AudiencesPage, isAudience } from 'components/audience/models/segmentModel';
import { segmentsApi } from 'components/audience/segmentsApi';
import {
    MESSAGE_MARKETING_AUDIENCE_CREATE_ERROR,
    MESSAGE_MARKETING_AUDIENCE_CREATE_SUCCESS,
    MESSAGE_MARKETING_AUDIENCE_UPDATE_ERROR,
    MESSAGE_MARKETING_AUDIENCE_UPDATE_SUCCESS
} from 'config/messages';
import logger from 'lib/logger';
import { isArrayOf } from 'lib/typeguards';
import { enqueueSnackbar } from 'notistack';
import { DEFAULT_PAGE_LIMIT } from 'lib/hooks/usePagedData';
import { QueryParameter } from 'lib/HttpClient';
import type { AppDispatch } from '../store';

export interface AudienceCreateRequestData {
    title: string;
    description?: string;
    type: string;
    expiryHorizon?: number;
}

export interface AudiencesState {
    isLoading: boolean;
    audiences: Audience[];
    summary?: AudiencesPage;
}

export const initialState: AudiencesState = {
    audiences: [],
    isLoading: true
};

const AUDIENCES_PER_PAGE = 20;

const audiencesSlice = createSlice({
    name: 'audiences',
    initialState,
    reducers: {
        startRequest(state) {
            state.isLoading = true;
        },
        endRequest(state) {
            state.isLoading = false;
        },
        getAudiencesError(state) {
            state.audiences = [];
        },
        getAudiencesSuccess(
            state,
            action: PayloadAction<{ audiences: Audience[]; summary?: AudiencesPage }>
        ) {
            state.audiences = action.payload.audiences;
            state.summary = action.payload.summary;
        },
        loadAudiencesSuccess(
            state,
            action: PayloadAction<{ audiences: Audience[]; summary?: AudiencesPage }>
        ) {
            state.audiences = [...state.audiences, ...action.payload.audiences];
            state.summary = action.payload.summary;
        },
        createAudienceSuccess(state, action: PayloadAction<{ audience: Audience }>) {
            state.audiences = [action.payload.audience, ...state.audiences];
        },
        updateAudienceSuccess(state, action: PayloadAction<{ audience: Audience }>) {
            state.audiences = state.audiences.map(item =>
                item._id === action.payload.audience._id
                    ? { count: item.count, metrics: item.metrics, ...action.payload.audience }
                    : item
            );
        }
    }
});

export const {
    startRequest,
    endRequest,
    getAudiencesError,
    getAudiencesSuccess,
    loadAudiencesSuccess,
    createAudienceSuccess,
    updateAudienceSuccess
} = audiencesSlice.actions;

export const getAudiences = () => async (dispatch: AppDispatch) => {
    dispatch(startRequest());
    const queryParameters = [{ key: 'limit', value: AUDIENCES_PER_PAGE }];
    const response = await segmentsApi.getList({ queryParameters });
    if (!response.ok || !isArrayOf(isAudience, response.body.items)) {
        logger.error('TypeError: expected Audience[], got:', response.body.items);
        dispatch(getAudiencesError());
    } else {
        dispatch(
            getAudiencesSuccess({
                audiences: response.body.items,
                summary: response.body.page
            })
        );
    }

    dispatch(endRequest());
};

export const getAllAudiences =
    (params?: { metrics?: boolean; count?: boolean }) => async (dispatch: AppDispatch) => {
        dispatch(startRequest());
        try {
            const pageReducer = async (startKey?: string) => {
                const queryParameters: QueryParameter[] = [
                    { key: 'limit', value: DEFAULT_PAGE_LIMIT },
                    { key: 'sort', value: '-updated' }
                ];
                if (params) {
                    const include = Object.entries(params)
                        .filter(([_, value]) => value)
                        .map(([key]) => key)
                        .join(',');

                    queryParameters.push({ key: 'include', value: include });
                }
                if (startKey) {
                    queryParameters.push({ key: 'startKey', value: startKey });
                }
                const response = await segmentsApi.getListV12({ queryParameters });

                if (!response.ok) {
                    throw new Error(`${response.statusCode} - Failed to fetch Audiences`);
                }

                // Parse body
                const {
                    page: { nextKey },
                    items
                } = response.body;
                if (startKey) {
                    dispatch(loadAudiencesSuccess({ audiences: items }));
                } else {
                    dispatch(getAudiencesSuccess({ audiences: items }));
                }
                if (nextKey) {
                    await pageReducer(nextKey);
                }
            };
            await pageReducer();
        } catch (e) {
            logger.error('Failed to load all audiences', e);
            dispatch(getAudiencesError());
        }

        dispatch(endRequest());
    };

export const loadAudiences = (startKey: string) => async (dispatch: AppDispatch) => {
    dispatch(startRequest());
    const queryParameters = [
        { key: 'limit', value: AUDIENCES_PER_PAGE },
        { key: 'startKey', value: startKey }
    ];
    const response = await segmentsApi.getList({ queryParameters });
    if (!response.ok || !isArrayOf(isAudience, response.body.items)) {
        logger.error('TypeError: expected Audience[], got:', response.body.items);
        dispatch(getAudiencesError());
    } else {
        dispatch(
            loadAudiencesSuccess({
                audiences: response.body.items,
                summary: response.body.page
            })
        );
    }

    dispatch(endRequest());
};

export const createAudience = (body: AudienceCreateRequestData) => async (dispatch: AppDispatch) => {
    dispatch(startRequest());
    try {
        const result = await segmentsApi.create({ body });
        if (!result.ok) {
            throw new Error(result.body.mesaage);
        }
        dispatch(createAudienceSuccess({ audience: result.body }));
        enqueueSnackbar(MESSAGE_MARKETING_AUDIENCE_CREATE_SUCCESS, { variant: 'success' });
        dispatch(endRequest());
        return result.body;
    } catch (e) {
        enqueueSnackbar(MESSAGE_MARKETING_AUDIENCE_CREATE_ERROR, { variant: 'error' });
        logger.error('Failed to create an action', e);
        dispatch(endRequest());
        return null;
    }
};

export const updateAudience = (id: string, data: Partial<Audience>) => async (dispatch: AppDispatch) => {
    dispatch(startRequest());
    try {
        const result = await segmentsApi.update(id, { body: data });
        if (!result.ok) {
            throw new Error(result.body.mesaage);
        }
        dispatch(updateAudienceSuccess({ audience: result.body }));
        enqueueSnackbar(MESSAGE_MARKETING_AUDIENCE_UPDATE_SUCCESS, { variant: 'success' });
        dispatch(endRequest());
        return result.body;
    } catch (e) {
        enqueueSnackbar(MESSAGE_MARKETING_AUDIENCE_UPDATE_ERROR, { variant: 'error' });
        logger.error('Failed to create an action', e);
        dispatch(endRequest());
        return null;
    }
};

export default audiencesSlice.reducer;
